import React, { type ErrorInfo, useMemo, useRef } from "react";
import { Icon, Loader, Message } from "semantic-ui-react";
import { type Props, type SankeyData, type SankeyFuncs } from "../../types/Sankey";
import { SankeyChart } from "../SankeyChart";

import "./sankeyWrapper.scss";
import miscellaneousUtils from "utils/miscellaneousUtils";

export type WrapperProps = {
  title: string;
  error?: string;
  loading?: boolean;
} & Props;

const hasItems = (graph: SankeyData) => graph?.Relations?.length > 0;

export const SankeyWrapper: React.FC<WrapperProps> = ({ title, loading, error, ...sankeyProps }) => {
  const sankeyRef = useRef<SankeyFuncs>(null);

  const zoomOut = () => {
    sankeyRef.current?.zoomOut();
  };

  const zoomIn = () => {
    sankeyRef.current?.zoomIn();
  };

  const body = useMemo(() => {
    if (loading) return <Loader className="sankeyLoader" active />;
    if (error) return <Message color="red">{error}</Message>;
    if (sankeyProps.data) {
      return (
        <SankeyErrorBoundary errorView={<Message color="red">An error occurred while rendering this chart.</Message>}>
          <div className="sankeyChildWrapper">
            {hasItems(sankeyProps.data) ? (
              <SankeyChart ref={sankeyRef} {...sankeyProps} />
            ) : (
              <div className="noSankeyData" data-testid="no sankey data">
                Once users begin this Flow, engagement data will appear here.
              </div>
            )}
          </div>
        </SankeyErrorBoundary>
      );
    }
  }, [error, loading, sankeyProps]);

  return (
    <div className="root">
      <div className="headerContainer">
        <h2 className="title">{title}</h2>
        <div className="actions">
          <span>Zoom</span>
          <Icon name="zoom in" data-testid="sankey zoom in" link onClick={zoomIn} />
          <Icon name="zoom out" data-testid="sankey zoom out" link onClick={zoomOut} />
        </div>
      </div>
      {body}
    </div>
  );
};

interface ErrorProps {
  errorView: React.ReactNode;
  children: React.ReactNode;
}

interface ErrorState {
  hasError: boolean;
}

class SankeyErrorBoundary extends React.Component<ErrorProps, ErrorState> {
  constructor(props: ErrorProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(_: Error) {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    miscellaneousUtils.logError(error.message, info.componentStack);
  }

  onErrorStateChanged = (hasError: boolean) => {
    this.setState({ hasError });
  };

  render() {
    if (this.state.hasError) {
      return this.props.errorView;
    }

    return this.props.children;
  }
}
